// helpers/ProjectHelpers.js
//
// Project selector helpers
// - Pure selectors over projects
// - No DataStore reads/writes
//

import { DataStore } from "@aws-amplify/datastore";
import { Projects } from "../models"; // adjust if your helpers folder is nested differently
import { PermissionType, ViewType } from "../Enums"; // Ensure ViewType is imported correctly
import * as LogHelpers from "./LogHelpers";
import * as UserHelpers from "./UserHelpers";
import * as ViewHelpers from "./ViewHelpers";


export const saveProjectPartial = async (projectToken, partial) => {
  if (!projectToken) throw new Error("saveProjectPartial: projectToken is required");
  if (!partial || typeof partial !== "object") {
    throw new Error("saveProjectPartial: partial update object is required");
  }

  let matches;
  try {
    matches = await DataStore.query(Projects, (p) => p.token.eq(projectToken));
  } catch (err) {
    console.error("DataStore.query (by token) failed:", err);
    throw err;
  }

  const current = Array.isArray(matches) ? matches[0] : null;
  if (!current) throw new Error(`saveProjectPartial: project not found for token ${projectToken}`);

  try {
    return await DataStore.save(
      Projects.copyOf(current, (draft) => {
        if (Object.prototype.hasOwnProperty.call(partial, "name")) draft.name = partial.name;
        if (Object.prototype.hasOwnProperty.call(partial, "abbreviation")) draft.abbreviation = partial.abbreviation;
        if (Object.prototype.hasOwnProperty.call(partial, "code")) draft.code = partial.code;
        if (Object.prototype.hasOwnProperty.call(partial, "accountToken")) draft.accountToken = partial.accountToken;

        if (Object.prototype.hasOwnProperty.call(partial, "meta")) draft.meta = partial.meta;
        if (Object.prototype.hasOwnProperty.call(partial, "prompts")) draft.prompts = partial.prompts;
        if (Object.prototype.hasOwnProperty.call(partial, "OZ")) draft.OZ = partial.OZ;
        if (Object.prototype.hasOwnProperty.call(partial, "config")) draft.config = partial.config;

        if (Object.prototype.hasOwnProperty.call(partial, "theme")) draft.theme = partial.theme;
        if (Object.prototype.hasOwnProperty.call(partial, "contacts")) draft.contacts = partial.contacts;
        if (Object.prototype.hasOwnProperty.call(partial, "timetable")) draft.timetable = partial.timetable;
      })
    );
  } catch (err) {
    console.error("DataStore.save failed:", err);
    throw err;
  }
};


// ============================================================================
// SECTION 1 — PURE FORM + VIEW SELECTORS (no DataStore reads/writes)
// ============================================================================

// ============================================================================
// SECTION 2 — BUSINESS / DERIVATION HELPERS (no DataStore writes)
// ============================================================================

// ============================================================================
// SECTION 2B — CURRENT FORM PROCESSING (no DataStore writes)
// ============================================================================
//
// This is the "shrunken" version of the PCF useEffect logic.
// It computes derived values and returns them.
// App.js stays responsible for calling setters + localStorage.
//
// IMPORTANT:
// - uses ViewType enum (no string comparisons)
// - does not call setXxx
// - does not access localStorage
//

// ============================================================================
// SECTION 3 — DATASTORE READ/WRITE HELPERS (mutating / persistence)
// ============================================================================

// Usage: await updateMessage(formId, { label, template|body, subject, bcc?, replyTo?, sender?, type?, recipients? })
// N.B. This will only work for the new style of messages stored as array in meta.messages2

// ============================================================================
// SECTION 4 — PRIVATE HELPERS (module-local)
// ============================================================================

// trim strings for comparison
const norm = (v) => (typeof v === "string" ? v.trim() : v);

// compare only the typical email-ish fields (keep it simple and robust)
const isChanged = (a = {}, b = {}) =>
  norm(a.template ?? a.body) !== norm(b.template ?? b.body) ||
  norm(a.subject) !== norm(b.subject) ||
  norm(a.replyTo) !== norm(b.replyTo) ||
  norm(a.sender) !== norm(b.sender) ||
  norm(a.bcc) !== norm(b.bcc);
